import React from 'react';
import './style.scss';
import UQIcon from '../icon';
import UQCheckbox from '../checkbox';

export interface UQDataGridColumn<T> {
  columnId: string;
  renderHeaderCell: () => React.ReactNode;
  renderCell: (item: T) => React.ReactNode;
  compare?: (a: T, b: T) => number;
  minWidth?: string;
}

export interface UQDataGridProps<T> {
  items: T[];
  columns: UQDataGridColumn<T>[];
  sortable?: boolean;
  selectionMode?: 'none' | 'single' | 'multiselect';
  getRowId?: (item: T) => string | number;
  onRowSelect?: (selectedRows: T[]) => void;
  onSort?: (column: UQDataGridColumn<T>) => void;
  showCheckbox?: boolean;
  rowLines?: boolean;
  headerColor?: string;
  compactCheckboxCell?: boolean;
  emptyMessage?: string; 
}

export const UQDataGrid = <T,>({
  items,
  columns,
  sortable = false,
  selectionMode = 'none',
  getRowId,
  onRowSelect,
  onSort,
  showCheckbox = false,
  rowLines = false,
  headerColor,
  compactCheckboxCell = false,
  emptyMessage,
}: UQDataGridProps<T>) => {
  const [sortedItems, setSortedItems] = React.useState(items);
  const [selectedRows, setSelectedRows] = React.useState<Set<string | number>>(new Set());
  const [sortConfig, setSortConfig] = React.useState<{ columnId: string; direction: 'asc' | 'desc' } | null>(null);

  const originalItems = React.useRef(items);

  React.useEffect(() => {
    setSortedItems(items);
    originalItems.current = items;
  }, [items]);

  const handleSort = (column: UQDataGridColumn<T>) => {
    if (!sortable || !column.compare) return;

    let direction: 'asc' | 'desc' | null = 'asc';
    if (sortConfig?.columnId !== column.columnId) {
      direction = 'asc';
    } else if (sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.direction === 'desc') {
      direction = null; // Unset sort
    }

    if (!direction) {
      setSortConfig(null);
      setSortedItems(originalItems.current);
    } else {
      const sorted = [...sortedItems].sort((a, b) =>
        direction === 'asc' ? column.compare!(a, b) : column.compare!(b, a)
      );
      setSortConfig({ columnId: column.columnId, direction });
      setSortedItems(sorted);
    }

    if (onSort) {
      onSort(column);
    }
  };

  const handleRowSelect = (rowId: string | number) => {
    if (selectionMode === 'none') return;
    console.log(`Row selected: ${rowId}`);

    const updatedSelection = new Set(selectedRows);
    if (updatedSelection.has(rowId)) {
      updatedSelection.delete(rowId);
    } else {
      if (selectionMode === 'single') {
        updatedSelection.clear();
      }
      updatedSelection.add(rowId);
    }

    setSelectedRows(updatedSelection);
    onRowSelect?.(items.filter((item) => updatedSelection.has(getRowId!(item))));
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allRowIds = items.map((item) => getRowId!(item));
      setSelectedRows(new Set(allRowIds));
      onRowSelect?.(items);
    } else {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    }
  };

  return (
    <div className={`uq-data-grid${rowLines ? ' uq-data-grid-row-lines' : ''}`}>
      <table>
        <thead>
          <tr>
            {showCheckbox && (
              <th
                className={`uq-data-grid-checkbox-cell${compactCheckboxCell ? ' compact' : ''
                  }`}>
                <UQCheckbox
                  checked={selectedRows.size === items.length}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.columnId}
                onClick={() => handleSort(column)}
                className={(sortable && column.compare ? 'sortable' : '') +
                  `${compactCheckboxCell ? ' compact' : ''
                  }`}
                style={{
                  ...(headerColor ? { color: headerColor } : {}),
                  textTransform: 'none',
                  ...(column.minWidth ? { minWidth: column.minWidth } : {}),
                }}
              >
                <div className="header-content">
                  {column.renderHeaderCell()}
                  <span className="sort-icon-wrapper">
                    {sortable && column.compare ? (
                      sortConfig?.columnId === column.columnId ? (
                        <UQIcon
                          icon={
                            sortConfig.direction === 'asc'
                              ? 'arrow-up-contained-01'
                              : 'arrow-down-contained-01'
                          }
                        />
                      ) : (
                        <span className="sort-icon-placeholder" />
                      )
                    ) : null}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedItems.length === 0 ? (
            emptyMessage ? (
              <tr>
                <td
                  colSpan={columns.length + (showCheckbox ? 1 : 0)}
                  style={{ textAlign: 'center', color: '#888' }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : null
          ) : (
            sortedItems.map((item, rowIndex) => {
              const rowId = getRowId ? getRowId(item) : rowIndex;
              const isSelected = selectedRows.has(rowId);

              return (
                <tr
                  key={rowId}
                  className={(isSelected ? 'selected' : '') + `${compactCheckboxCell ? ' compact' : ''
                    }`}
                  onClick={() => rowId && handleRowSelect(rowId)}
                >
                  {showCheckbox && (
                    <td
                      className={`uq-data-grid-checkbox-cell${compactCheckboxCell ? ' compact' : ''
                        }`}
                    >
                      <UQCheckbox
                        checked={isSelected}
                        onClick={e => e.stopPropagation()}
                        onChange={(_checked, e) => {
                          e?.stopPropagation();
                        }}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={`${rowId}-${column.columnId}`}
                      style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                    >
                      {column.renderCell(item)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UQDataGrid;